// Rastreio (Cotacoes > Rastreio): escolhe o produto e mostra pra onde foi o que entrou na ULTIMA
// compra — cada venda depois dela, com pedido, cliente e nota fiscal, e a conta do estoque fechando.
// Le o razao de estoque ([DA-595]) pela rota `GET /api/produtos/:id/rastreio`; e' a mesma corrente da
// aba Movimentacoes da ficha, so que cortada na ultima nota de entrada e agrupada por pedido.
import { useEffect, useRef, useState } from 'react';
import { Route, Search, X } from 'lucide-react';
import { formatDataSP, formatQtd } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { FotoProduto } from '../../components/FotoProduto';
import { useListaTeclado } from '../../lib/useListaTeclado';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo } from '../../lib/produto-inativo';
import { rotuloMovimentacao } from '../produtos/movimentacao-rotulos';

type ProdutoBusca = {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  estoque_atual: string;
  status?: string;
  foto_url?: string | null;
};

type Venda = {
  orcamento_id: number;
  numero: string;
  status: string;
  qtd: string;
  data: string;
  cliente_nome: string | null;
  vendedor_login: string | null;
  nfce: number[] | null;
  nfe: number[] | null;
};

type Rastreio = {
  produto: { id: number; codigo_interno: string | null; descricao: string; estoque_atual: string };
  compra: null | {
    nota_id: number;
    numero: string;
    data_emissao: string | null;
    fornecedor_nome: string | null;
    entrou_em: string;
    estoque_atualizado: boolean;
    qtd_entrada: string | null;
    estoque_antes: string | null;
    estoque_depois: string | null;
  };
  vendas: Venda[];
  outros: { tipo: string; n: number; delta: string | null }[];
};

const CHAVE_PRODUTO = 'rastreio_produto_id';

// Quantidade em milesimos inteiros: a conta do estoque compara dois numeros e nao pode errar no
// ponto flutuante (o razao grava 3 casas).
function mil(v: string | number | null | undefined): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? Math.round(n * 1000) : 0;
}
const qtd = (m: number) => formatQtd(m / 1000);

function notaFiscal(v: Venda): string {
  const partes = [
    ...(v.nfce ?? []).map((n) => `NFC-e ${n}`),
    ...(v.nfe ?? []).map((n) => `NF-e ${n}`),
  ];
  if (partes.length) return partes.join(', ');
  if (v.status === 'enviado_caixa') return 'Pedido no caixa, ainda nao pago';
  if (v.status === 'finalizado') return 'Sem nota';
  return `Sem nota (pedido ${v.status})`;
}

export function RastreioProduto() {
  const dialog = useDialog();
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<ProdutoBusca[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [produtoId, setProdutoId] = useState<number | null>(() => {
    try {
      const s = localStorage.getItem(CHAVE_PRODUTO);
      if (s === null || s === '') return null;
      const n = Number(s);
      return Number.isInteger(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  });
  const [dados, setDados] = useState<Rastreio | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const reqBusca = useRef(0);
  const reqRastreio = useRef(0);

  useEffect(() => {
    try {
      if (produtoId) localStorage.setItem(CHAVE_PRODUTO, String(produtoId));
      else localStorage.removeItem(CHAVE_PRODUTO);
    } catch { /* sem armazenamento a tela funciona igual, so nao lembra */ }
  }, [produtoId]);

  useEffect(() => {
    const t = busca.trim();
    const req = ++reqBusca.current;
    if (t.length < 2) {
      setResultados([]);
      setBuscando(false);
      return;
    }
    setBuscando(true);
    const timer = setTimeout(() => {
      api.get<ProdutoBusca[]>(`/api/produtos?q=${encodeURIComponent(t)}&com_inativos=1`)
        .then((r) => { if (req === reqBusca.current) setResultados((r || []).slice(0, 20)); })
        .catch(() => { if (req === reqBusca.current) setResultados([]); })
        .finally(() => { if (req === reqBusca.current) setBuscando(false); });
    }, 250);
    return () => clearTimeout(timer);
  }, [busca]);

  useEffect(() => {
    const req = ++reqRastreio.current;
    if (!produtoId) {
      setDados(null);
      return;
    }
    setCarregando(true);
    setErro(null);
    api.get<Rastreio>(`/api/produtos/${produtoId}/rastreio`)
      .then((r) => { if (req === reqRastreio.current) setDados(r); })
      .catch((e: any) => {
        if (req !== reqRastreio.current) return;
        setDados(null);
        setErro(e?.message || 'Nao consegui carregar o rastreio');
      })
      .finally(() => { if (req === reqRastreio.current) setCarregando(false); });
  }, [produtoId]);

  function escolher(p: ProdutoBusca) {
    // Bloqueia tambem aqui, mesmo sendo so consulta — decisao dele, 28/10/2026.
    if (produtoInativo(p)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
    setProdutoId(p.id);
    setBusca('');
    setResultados([]);
  }
  const teclado = useListaTeclado(resultados, escolher);

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <Route size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} /> Rastreio
        </h2>
      </div>

      <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
        Escolha o produto: a tela mostra a <strong>ultima compra</strong> e cada <strong>venda depois
        dela</strong>, com o pedido, o cliente e a nota fiscal.
      </p>

      <div style={{ maxWidth: 640, marginTop: 12 }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.6 }} />
          <input
            className="w-full pl-9"
            placeholder="Digite o codigo ou a descricao do produto..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={teclado.onKeyDown}
            autoFocus
          />
        </div>
        {busca.trim().length >= 2 && (
          <div
            ref={teclado.listaRef}
            style={{
              marginTop: 4, maxHeight: 320, overflowY: 'auto', borderRadius: 4,
              border: '1px solid var(--page-bg)', backgroundColor: 'var(--panel-bg)',
            }}
          >
            {buscando && resultados.length === 0 ? (
              <div className="muted" style={{ padding: '8px 12px', fontSize: 13 }}>Buscando...</div>
            ) : resultados.length === 0 ? (
              <div className="muted" style={{ padding: '8px 12px', fontSize: 13 }}>Nenhum produto encontrado.</div>
            ) : resultados.map((p, idx) => (
              <div
                key={p.id}
                data-idx={idx}
                onMouseEnter={() => teclado.setHighlight(idx)}
                onClick={() => escolher(p)}
                style={{
                  padding: '4px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', gap: 10, alignItems: 'center',
                  ...(idx === teclado.highlight ? { backgroundColor: '#fde68a', color: '#000' } : {}),
                }}
              >
                {p.foto_url
                  ? <FotoProduto url={p.foto_url} tam={40} style={{ borderRadius: 3, flex: '0 0 auto' }} />
                  : <span style={{ width: 40, height: 40, flex: '0 0 auto', borderRadius: 3, border: '1px dashed #94a3b8' }} title="Sem foto" />}
                <span style={{ fontWeight: 700, minWidth: 48 }}>{p.codigo_interno || '—'}</span>
                <span style={{ flex: 1 }}>{p.descricao}</span>
                {p.status === 'inativo' && (
                  <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3, backgroundColor: '#94a3b8', color: '#000' }}>INATIVO</span>
                )}
                <span style={{ whiteSpace: 'nowrap' }}>estoque {formatQtd(Number(p.estoque_atual ?? 0))}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {erro && (
        <p style={{ background: '#f97316', color: '#000', padding: '8px 12px', borderRadius: 3, fontSize: 13, marginTop: 16 }}>{erro}</p>
      )}

      {produtoId && carregando && !dados && <p className="muted" style={{ marginTop: 16 }}>Carregando...</p>}

      {dados && <Resultado dados={dados} onLimpar={() => setProdutoId(null)} />}
    </div>
  );
}

function Resultado({ dados, onLimpar }: { dados: Rastreio; onLimpar: () => void }) {
  const { produto, compra } = dados;
  const vendidas = dados.vendas.filter((v) => mil(v.qtd) > 0);
  const voltaram = dados.vendas.filter((v) => mil(v.qtd) === 0).length;
  // Pedido que baixou ANTES da compra e voltou do caixa DEPOIS dela: devolve ao estoque dentro do
  // periodo. Nao e' venda deste lote, mas entra na conta pra ela fechar.
  const devolvidosAnteriores = dados.vendas.filter((v) => mil(v.qtd) < 0);
  const totalVendido = vendidas.reduce((s, v) => s + mil(v.qtd), 0);

  const composicao = [
    ...dados.outros
      .filter((o) => mil(o.delta) !== 0)
      .map((o) => ({ rotulo: rotuloMovimentacao(o.tipo), n: o.n, delta: mil(o.delta) })),
    ...(devolvidosAnteriores.length
      ? [{
        rotulo: 'Pedido anterior a compra que voltou do caixa',
        n: devolvidosAnteriores.length,
        delta: -devolvidosAnteriores.reduce((s, v) => s + mil(v.qtd), 0),
      }]
      : []),
  ];

  const temEntrada = compra?.qtd_entrada != null && compra.estoque_depois != null;
  const estoqueHoje = mil(produto.estoque_atual);
  const calculado = temEntrada
    ? mil(compra!.estoque_depois) - totalVendido + composicao.reduce((s, c) => s + c.delta, 0)
    : null;

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>
          <span style={{ fontWeight: 700 }}>{produto.codigo_interno || '—'}</span> · {produto.descricao}
        </h3>
        <button type="button" className="btn-icon" title="Escolher outro produto" onClick={onLimpar}>
          <X size={16} />
        </button>
      </div>

      {!compra ? (
        <p className="muted">Este produto nao aparece em nenhuma nota de entrada — nao ha compra pra rastrear.</p>
      ) : (
        <>
          <div style={{ backgroundColor: 'var(--panel-bg)', borderRadius: 6, padding: '12px 16px', fontSize: 14, lineHeight: 1.7, maxWidth: 820 }}>
            <div>
              <strong>Ultima compra:</strong> {formatDataSP(compra.entrou_em)} — nota {compra.numero}
              {compra.fornecedor_nome ? ` · ${compra.fornecedor_nome}` : ''}
              {compra.data_emissao ? <span className="muted"> (emitida em {compra.data_emissao})</span> : null}
            </div>
            {temEntrada ? (
              <div>
                <strong>Entraram {qtd(mil(compra.qtd_entrada))}</strong>
                <span className="muted"> (tinha {qtd(mil(compra.estoque_antes))}, ficou {qtd(mil(compra.estoque_depois))})</span>
              </div>
            ) : (
              <div className="muted">
                {compra.estoque_atualizado
                  ? 'Esta nota e anterior a 21/08/2026, quando o sistema ainda nao registrava quanto entrou no estoque — a conta abaixo nao tem o ponto de partida.'
                  : 'Esta nota foi importada sem dar entrada no estoque.'}
              </div>
            )}
            <div>
              <strong>Depois disso saiu {qtd(totalVendido)}</strong> em {vendidas.length} {vendidas.length === 1 ? 'venda' : 'vendas'}
            </div>
            {composicao.map((c) => (
              <div key={c.rotulo}>
                {c.rotulo}: {c.delta > 0 ? '+' : '−'}{qtd(Math.abs(c.delta))}
                <span className="muted"> ({c.n} {c.n === 1 ? 'movimento' : 'movimentos'})</span>
              </div>
            ))}
            <div><strong>Estoque hoje: {qtd(estoqueHoje)}</strong></div>
            {calculado !== null && (
              calculado === estoqueHoje ? (
                <div className="muted">A conta fecha: tudo o que entrou tem saida registrada.</div>
              ) : (
                <div style={{ background: '#f97316', color: '#000', padding: '4px 10px', borderRadius: 3, marginTop: 4 }}>
                  A conta nao fecha: pelos movimentos o estoque seria {qtd(calculado)}, e o cadastro diz {qtd(estoqueHoje)}
                  {' '}(diferenca de {qtd(Math.abs(estoqueHoje - calculado))}).
                </div>
              )
            )}
          </div>

          {vendidas.length === 0 ? (
            <p className="muted" style={{ marginTop: 16 }}>Nenhuma venda depois desta compra.</p>
          ) : (
            <div className="overflow-x-auto" style={{ marginTop: 16 }}>
              <table className="tabela">
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>Data</th>
                    <th style={{ width: 120 }}>Pedido</th>
                    <th>Cliente</th>
                    <th style={{ width: 120 }}>Vendedor</th>
                    <th style={{ width: 80, textAlign: 'right' }}>Qtd</th>
                    <th style={{ width: 220 }}>Nota fiscal</th>
                  </tr>
                </thead>
                <tbody>
                  {vendidas.map((v) => (
                    <tr key={v.orcamento_id}>
                      <td>{formatDataSP(v.data)}</td>
                      <td>{v.numero}</td>
                      <td>{v.cliente_nome || '—'}</td>
                      <td>{v.vendedor_login || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{qtd(mil(v.qtd))}</td>
                      <td>{notaFiscal(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {voltaram > 0 && (
            <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              {voltaram} {voltaram === 1 ? 'pedido foi' : 'pedidos foram'} ao caixa e {voltaram === 1 ? 'voltou' : 'voltaram'} sem
              venda — nao {voltaram === 1 ? 'entra' : 'entram'} na lista.
            </p>
          )}
        </>
      )}
    </div>
  );
}
